import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSeasonStore } from '@/stores/season-store';
import { useTemplateStore } from '@/stores/template-store';
import { usePropertyStore } from '@/stores/property-store';
import { useTaskStore } from '@/stores/task-store';
import type { WeaponType } from '@/types/season';
import { WeaponType as WeaponTypeEnum } from '@/types/season';
import type { Season } from '@/types/season';
import type { Property } from '@/types/property';
import { Button } from '@/components/common/Button';
import { format, parseISO } from 'date-fns';
import { getStateName } from '@/utils/us-states';

type Step = 'season' | 'property' | 'confirm';

export interface TemplateSelectionPayload {
  templateId: string;
  seasonId: string;
  propertyId?: string;
}

interface TemplateSelectionFlowProps {
  templateId: string;
  templateName: string;
  weaponType: WeaponType;
  preselectedSeasonId?: string;
  onClose: () => void;
}

function weaponLabel(w: string): string {
  const map: Record<string, string> = {
    gun: 'Gun', bow: 'Bow', muzzleloader: 'Muzzleloader', crossbow: 'Crossbow',
    youth: 'Youth', any: 'Any', other: 'Other',
  };
  return map[w] ?? w;
}

export function TemplateSelectionFlow({
  templateId,
  templateName,
  weaponType,
  preselectedSeasonId,
  onClose,
}: TemplateSelectionFlowProps) {
  const navigate = useNavigate();
  const { seasons, loadSeasons } = useSeasonStore();
  const { templates, loadTemplates } = useTemplateStore();
  const { properties, loadProperties } = usePropertyStore();
  const { generateFromTemplate } = useTaskStore();

  const [step, setStep] = useState<Step>(preselectedSeasonId ? 'property' : 'season');
  const [selectedSeasonId, setSelectedSeasonId] = useState<string>(preselectedSeasonId ?? '');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    if (seasons.length === 0) void loadSeasons();
    if (templates.length === 0) void loadTemplates();
    if (properties.length === 0) void loadProperties();
  }, [seasons.length, loadSeasons, templates.length, loadTemplates, properties.length, loadProperties]);

  // M-04: when the template is tagged Any, treat it as a wildcard matching every
  // configured season. Otherwise, match seasons that share the weapon type or
  // are themselves tagged Any.
  const matchingSeasons = seasons.filter((s) =>
    weaponType === WeaponTypeEnum.Any
      ? true
      : s.weaponType === weaponType || s.weaponType === WeaponTypeEnum.Any,
  );

  // M-04: empty-state copy is conditional. For Any-tagged templates, the
  // dynamic "No any seasons" / "Create a any season" copy is grammatically
  // broken — use a static "No seasons configured." message instead.
  const isAnyTemplate = weaponType === WeaponTypeEnum.Any;
  const emptyStateHeading = isAnyTemplate
    ? 'No seasons configured.'
    : `No ${weaponLabel(weaponType).toLowerCase()} seasons configured.`;
  const emptyStateBody = isAnyTemplate
    ? 'Create a season first, then return here to generate tasks.'
    : `Create a ${weaponLabel(weaponType).toLowerCase()} season first, then return here to generate tasks.`;

  const selectedSeason: Season | undefined = seasons.find((s) => s.id === selectedSeasonId);
  const selectedProperty: Property | undefined = properties.find(
    (p) => p.id === selectedPropertyId,
  );

  async function handleConfirm() {
    if (!selectedSeasonId) return;
    setIsSubmitting(true);
    try {
      await generateFromTemplate(templateId, selectedSeasonId, selectedPropertyId);
      navigate(`/seasons/${selectedSeasonId}`);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate tasks. Please try again.';
      setToastMessage(message);
      setIsSubmitting(false);
    }
  }

  return (
    <div
      data-testid="selection-flow-backdrop"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        data-testid="selection-flow"
        className="bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-t-xl sm:rounded-xl shadow-card-lg w-full max-w-md flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--color-border)] flex-shrink-0">
          <h2 className="font-display text-display-sm text-[var(--color-text-display)]">
            Use Template
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* ── Season picker ── */}
          {step === 'season' && (
            <div className="flex flex-col gap-4">
              <div>
                <p className="field-label">
                  Step 1 of 3 — Select Season
                </p>
                <p className="text-body-sm text-[var(--color-text-secondary)] mt-1">
                  {isAnyTemplate
                    ? 'Choose a season to generate tasks for.'
                    : `Choose a ${weaponLabel(weaponType).toLowerCase()} season to generate tasks for.`}
                </p>
              </div>

              {matchingSeasons.length === 0 ? (
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-2">
                  <p className="text-body-sm text-[var(--color-text-secondary)]">
                    {emptyStateHeading}
                  </p>
                  <p className="text-body-xs text-[var(--color-text-muted)]">
                    {emptyStateBody}
                  </p>
                  <a
                    href="/seasons/new"
                    className="text-body-xs text-[var(--color-accent)] hover:underline"
                    data-testid="create-season-link"
                  >
                    Create a season →
                  </a>
                </div>
              ) : (
                <div className="flex flex-col gap-2" data-testid="season-list">
                  {matchingSeasons.map((season) => (
                    <button
                      key={season.id}
                      data-testid={`season-option-${season.id}`}
                      onClick={() => setSelectedSeasonId(season.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedSeasonId === season.id
                        ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)]'
                        : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-border-subtle)]'
                        }`}
                    >
                      <p className="text-body-sm font-medium text-[var(--color-text-primary)]">
                        {season.name}
                      </p>
                      <p className="text-body-xs text-[var(--color-text-muted)] mt-0.5">
                        Opens {format(parseISO(season.openerDate), 'MMM d, yyyy')} · {season.state}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Property picker ── */}
          {step === 'property' && (
            <div className="flex flex-col gap-4">
              <div>
                <p className="field-label">
                  Step 2 of 3 — Select Property (Optional)
                </p>
                <p className="text-body-sm text-[var(--color-text-secondary)] mt-1">
                  Property-tied tasks will be linked to your selected property.
                </p>
              </div>

              <div className="flex flex-col gap-2" data-testid="property-list">
                {/* Skip option */}
                <button
                  data-testid="property-option-skip"
                  onClick={() => setSelectedPropertyId(undefined)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedPropertyId === undefined
                    ? 'border-[var(--color-accent)] bg-ember-900/20'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-border-subtle)]'
                    }`}
                >
                  <p className="text-body-sm font-medium text-[var(--color-text-primary)]">
                    Apply to all properties
                  </p>
                  <p className="text-body-xs text-[var(--color-text-muted)] mt-0.5">
                    Property-tied tasks will not be linked to a specific property
                  </p>
                </button>

                {properties.map((property) => (
                  <button
                    key={property.id}
                    data-testid={`property-option-${property.id}`}
                    onClick={() => setSelectedPropertyId(property.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedPropertyId === property.id
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)]'
                      : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-border-subtle)]'
                      }`}
                  >
                    <p className="text-body-sm font-medium text-[var(--color-text-primary)]">
                      {property.name}
                    </p>
                    {property.state && (
                      <p className="text-body-xs text-[var(--color-text-muted)] mt-0.5">
                        {getStateName(property.state)}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Confirmation ── */}
          {step === 'confirm' && (
            <div className="flex flex-col gap-4" data-testid="confirm-step">
              <div>
                <p className="field-label">
                  Step 3 of 3 — Confirm
                </p>
                <p className="text-body-sm text-[var(--color-text-secondary)] mt-1">
                  Review your selections before generating tasks.
                </p>
              </div>

              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-3">
                <div>
                  <p className="text-body-xs text-[var(--color-text-muted)] uppercase tracking-[0.08em]">Template</p>
                  <p className="text-body-sm text-[var(--color-text-primary)] mt-0.5">{templateName}</p>
                </div>
                <div>
                  <p className="text-body-xs text-[var(--color-text-muted)] uppercase tracking-[0.08em]">Season</p>
                  <p className="text-body-sm text-[var(--color-text-primary)] mt-0.5">
                    {selectedSeason?.name}
                    {selectedSeason && (
                      <span className="text-[var(--color-text-muted)]">
                        {' '}— Opens {format(parseISO(selectedSeason.openerDate), 'MMM d, yyyy')}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-body-xs text-[var(--color-text-muted)] uppercase tracking-[0.08em]">Property</p>
                  <p className="text-body-sm text-[var(--color-text-primary)] mt-0.5">
                    {selectedProperty?.name ?? 'All properties'}
                  </p>
                </div>
              </div>

              {toastMessage && (
                <div
                  data-testid="stub-toast"
                  className="bg-[var(--color-done-bg)] text-[var(--color-done)] rounded-lg p-3 text-body-sm"
                >
                  {toastMessage}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-[var(--color-border)] flex gap-3 flex-shrink-0">
          {step === 'season' && (
            <>
              <Button variant="ghost" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!selectedSeasonId}
                onClick={() => setStep('property')}
                className="flex-1"
                data-testid="next-to-property"
              >
                Next
              </Button>
            </>
          )}

          {step === 'property' && (
            <>
              <Button variant="ghost" onClick={() => setStep('season')} className="flex-1">
                Back
              </Button>
              <Button
                variant="primary"
                onClick={() => setStep('confirm')}
                className="flex-1"
                data-testid="next-to-confirm"
              >
                Next
              </Button>
            </>
          )}

          {step === 'confirm' && !toastMessage && (
            <>
              <Button variant="ghost" onClick={() => setStep('property')} className="flex-1">
                Back
              </Button>
              <Button
                variant="primary"
                disabled={isSubmitting}
                onClick={() => void handleConfirm()}
                className="flex-1"
                data-testid="generate-tasks-btn"
              >
                {isSubmitting ? 'Generating...' : 'Generate Tasks'}
              </Button>
            </>
          )}

          {step === 'confirm' && toastMessage && (
            <Button variant="ghost" onClick={onClose} className="flex-1">
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
